/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * Holly 3.0 Registration & Post-Registration spec — entry point at /register.
 *
 * Drives a state machine across five screens:
 *  - classifying  → FE-only: scan localStorage Holly sessions + floxKnown flag
 *  - router       → §4 four-button "What brings you to FLOX today?"
 *  - holly-chat   → §4.3 AISidebar opens inline with declaredIntent set; user
 *                   chats until Holly's "Continue to FLOX" register CTA fires
 *  - welcome-back → §3.3 returning-lead banner
 *  - register     → §5 four-field journey-aware form (with magic-link toggle)
 *  - success      → handoff confirmation, navigates to locally-computed redirect
 *
 * Pre-existing 8-field business-name/phone/role flow is preserved as
 * `LegacyRegistration` for parity-checking and potential rollback.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  CheckIcon,
  MapIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  Squares2X2Icon,
  SparklesIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  FLOX_KNOWN_BROWSER_KEY,
  FLOX_LOGO_BLUE_SRC,
  REGISTRATION_URL,
} from '@constants';
import { useAuth } from '@context';
import { useAuthHttp } from '@hooks';
import { AISidebar } from '@components/Holly';
import { Button } from '../../tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import {
  toTransportQuoteSummary,
  type RawHollyTransportFlowState,
} from '@forms/TransportOrderForm/hollyPrefill';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Journey =
  | 'warehouse_search'
  | 'transport_booking'
  | 'lsp_listing'
  | 'else';
type FlowState =
  | 'classifying'
  | 'router'
  | 'holly-chat'
  | 'welcome-back'
  | 'register'
  | 'success';

type Classification =
  | 'unknown'
  | 'returning_registered'
  | 'returning_session_active';

interface ClassifyResult {
  classification: Classification;
  // Populated only for `returning_session_active` — the active Holly thread we
  // can resume the user back into.
  priorSessionId?: string | null;
  priorSessionDate?: string | null;
}

interface TransportQuotePrefill {
  origin?: string;
  destination?: string;
  pallets?: number;
  date?: string;
  estimateLow?: number;
  estimateHigh?: number;
  currency?: string;
}

/**
 * Shape of `flowState` returned by GET /hollyv4/session/:id. Holly v4 keeps v3's flat
 * TransportBookingFlowState (the `RawHollyTransportFlowState` fields) plus the
 * legacy warehouse/quote pointers used by `computeRedirectTo`.
 */
type HollySessionFlowStateResponse = RawHollyTransportFlowState & {
  shortlistId?: string;
  quoteId?: string;
  declaredTrack?: string;
};

interface HollySessionPrefill {
  name?: string;
  email?: string;
  warehousesCount?: number;
  // Spec §6.2 — populated by Holly's transport-quote flow. Renders the
  // "Your quote from Holly" banner above the register form.
  transportQuote?: TransportQuotePrefill;
}

/**
 * Subset of the Holly session shape relevant to computing the post-registration
 * redirect — kept locally so the frontend can decide where to land the user
 * without a backend round-trip after `/Auth/Register`.
 */
interface HollySessionForRedirect {
  flow?: string;
  declaredIntent?: string;
  flowState?: {
    shortlistId?: string;
    quoteId?: string;
    declaredTrack?: string;
  };
}

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  hasAgreedToTandC: boolean;
}

interface RegisterApiResult {
  registrationResult: {
    wasSuccessful: boolean;
    user?: { id?: string; email: string };
    errors?: string[];
  };
  auth?: {
    accessToken?: string;
    refreshToken?: string;
    tokenExpiry?: string;
    notificationToken?: string;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Journey copy (spec §5.3)
// ─────────────────────────────────────────────────────────────────────────────

const JOURNEY_COPY: Record<
  Journey,
  {
    badge: string;
    headline: string;
    subcopy: (n: number) => string;
    cta: string;
  }
> = {
  warehouse_search: {
    badge: 'Warehouse search',
    headline: 'Create your account to unlock your shortlist',
    subcopy: (n) =>
      `Create a password — we already have the rest. Once registered, you'll see full provider details for all ${n} warehouses Holly found, with direct messaging and contract tools.`,
    cta: 'Create account and see shortlist',
  },
  transport_booking: {
    badge: 'Book transport',
    headline: 'Create your account to confirm your booking',
    subcopy: () =>
      "Create a password and you're ready to book. Your quote is saved and will be on the next screen.",
    cta: 'Create account and continue booking',
  },
  lsp_listing: {
    badge: 'List my services',
    headline: 'Create your account to list your services',
    subcopy: () =>
      "Create a password and we'll take you to profile setup. No monthly fees — you only pay when we match you with business.",
    cta: 'Create account and set up listings',
  },
  else: {
    badge: 'Direct sign up',
    headline: 'Create your FLOX account',
    subcopy: () =>
      "Just four fields. We'll ask about your organisation when we need to — not before.",
    cta: 'Create account',
  },
};

/**
 * §4 — seed prompt sent silently to Holly on the first turn of each journey.
 * Reuses the same directive copy already proven on the standalone embed buttons
 * (see `frontend/findWarehousingPromptButton/*Embed.tsx` and siblings) so the
 * registration router and the marketing-site embeds open Holly into identical
 * flows. "Else" maps to the platform-ops directive — explicitly worded for
 * "unrelated query for a user seeking industry insights".
 */
const ROUTER_PROMPTS: Record<Journey, string> = {
  warehouse_search:
    'Please find warehousing services for a Shipper/Vendor. Request a location (city, town, or postcode) to search for available warehouses',
  transport_booking:
    'Please help a Shipper or Vendor request transportation services to move goods between two locations. Ask for collection and delivery postcodes, load details, timing, and vehicle requirements to generate a transport quote.',
  lsp_listing:
    'Please guide a Logistics Service Provider on listing their warehouse or logistics services on FLOX',
  else: 'Please provide general logistics sector knowledge or handle an unrelated query for a user seeking industry insights',
};

// localStorage keys
const HOLLY_SESSION_IDS_KEY = 'floxHollySessionIds';

function readSessionIdsFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(HOLLY_SESSION_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

// Validation schema for the 4-field form (§5.1: password ≥10 chars)
const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .min(1, 'First name required')
    .required('First name required'),
  lastName: Yup.string()
    .trim()
    .min(1, 'Last name required')
    .required('Last name required'),
  email: Yup.string().email('Enter a valid email').required('Email required'),
  password: Yup.string()
    .min(10, 'At least 10 characters')
    .required('Password required'),
  hasAgreedToTandC: Yup.boolean().oneOf(
    [true],
    'You must accept the terms and conditions'
  ),
});

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function Registration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authHttp = useAuthHttp();
  const { logIn, setUser } = useAuth();

  const urlHollySessionId =
    searchParams.get('hollyState') || searchParams.get('hollySessionId');
  const journeyParam = (searchParams.get('journey') as Journey | null) || null;

  const [flowState, setFlowState] = useState<FlowState>('classifying');
  const [journey, setJourney] = useState<Journey>(journeyParam || 'else');
  const [classification, setClassification] = useState<ClassifyResult | null>(
    null
  );
  const [prefill, setPrefill] = useState<HollySessionPrefill>({});
  // Captured during prefill so we can compute the post-registration redirect
  // locally after /Auth/Register succeeds, without a second backend round-trip.
  const [hollySession, setHollySession] =
    useState<HollySessionForRedirect | null>(null);
  const [serverError, setServerError] = useState<string>('');
  const [redirectTo, setRedirectTo] = useState<string>('/noticeboard');
  // Holly 3.0 Reg §4 — threadId captured from the inline AISidebar handoff. When
  // the user transitions router → holly-chat → register, this is the session we
  // hydrate prefill from and pass to the backend on /Auth/Register.
  const [chatThreadId, setChatThreadId] = useState<string | null>(null);

  // Effective Holly session ID: URL deep-link wins (legacy /register?hollyState=X
  // path), otherwise the one captured from the in-page chat.
  const hollySessionId = urlHollySessionId || chatThreadId;

  // ── Mount: classify visitor + (optionally) hydrate from Holly session ────────
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      // Holly chat → register handoff via legacy URL params (email, name, role,
      // journey). When Holly emits a register CTA from outside the /register
      // page, the user lands here with prefill data on the query string. Skip
      // the classifier and go straight to the journey-themed register form.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const urlHandoff = parseUrlHandoff(searchParams);
      if (urlHandoff && !hollySessionId) {
        if (!cancelled) {
          setPrefill(urlHandoff.prefill);
          if (!journeyParam) setJourney(urlHandoff.journey);
          setFlowState('register');
        }
        return;
      }

      // If the URL has hollyState, we go straight to the register screen and
      // hydrate pre-fill from the Holly session — no classifier round-trip.
      // The URL-param handoff (email/name/role) is applied FIRST as the floor:
      // Holly's register CTA carries them on the query string precisely so the
      // form still prefills when the session lookup fails (e.g. the session
      // lives in a Holly version this env isn't serving). Session data, when it
      // arrives, only enriches it.
      if (hollySessionId) {
        if (urlHandoff && !cancelled) {
          setPrefill(urlHandoff.prefill);
          if (!journeyParam) setJourney(urlHandoff.journey);
        }
        try {
          const { data } = await authHttp.get<{
            sessionId: string;
            flow?: string;
            declaredIntent?: string;
            capturedContact?: { name?: string; email?: string };
            warehouses?: unknown[];
            flowState?: HollySessionFlowStateResponse;
          }>(`/hollyv4/session/${encodeURIComponent(hollySessionId)}`, {
            baseURL: '/',
          });
          if (cancelled) return;
          setPrefill({
            name: data.capturedContact?.name ?? urlHandoff?.prefill.name,
            email: data.capturedContact?.email ?? urlHandoff?.prefill.email,
            warehousesCount: Array.isArray(data.warehouses)
              ? data.warehouses.length
              : undefined,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            transportQuote: extractTransportQuote(data.flowState),
          });
          setHollySession({
            flow: data.flow,
            declaredIntent: data.declaredIntent,
            flowState: data.flowState,
          });
          if (!journeyParam && data.flow) {
            setJourney(mapFlowToJourney(data.flow));
          }
        } catch {
          // Hydration failure is not fatal — the URL-param prefill applied
          // above still stands, so a Holly handoff keeps the "visitor only
          // sets a password" contract even when the session can't be read.
        }
        if (!cancelled) setFlowState('register');
        return;
      }

      // Otherwise, classify the visitor entirely on the client. Two signals,
      // checked in priority order:
      //   1. Mechanism A — any localStorage Holly session that's still
      //      `active` and unexpired → resume Holly directly.
      //   2. Mechanism C — `floxKnown` localStorage flag set on prior
      //      login/register → "welcome back, log in" UX.
      // Mechanism B (typed-email lead lookup) was deliberately removed
      // along with the backend classifier: it required server-only
      // privacy guarantees that an FE-only path can't provide.
      try {
        const activeSession = await findActiveHollySession(
          authHttp,
          readSessionIdsFromStorage()
        );
        if (cancelled) return;
        if (activeSession) {
          setClassification({
            classification: 'returning_session_active',
            priorSessionId: activeSession.sessionId,
            priorSessionDate: activeSession.lastActiveAt ?? null,
          });
          navigate(
            `/embed?threadId=${encodeURIComponent(activeSession.sessionId)}`,
            { replace: true }
          );
          return;
        }
        if (localStorage.getItem(FLOX_KNOWN_BROWSER_KEY) === '1') {
          setClassification({ classification: 'returning_registered' });
          setFlowState('welcome-back');
          return;
        }
        setFlowState('router');
      } catch {
        // Any unexpected failure: fall back to the router so /register is
        // never blocked behind a classifier outage.
        if (!cancelled) setFlowState('router');
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hollySessionId]);

  // ── Holly chat → register handoff (§4 → §5) ─────────────────────────────────
  // Fired when the user clicks Holly's "Continue to FLOX" CTA inside AISidebar.
  // We capture the threadId and hydrate prefill from the just-completed session
  // before flipping to the register screen.
  const handleReadyToRegister = useCallback(
    async (threadId: string) => {
      setChatThreadId(threadId);
      try {
        const { data } = await authHttp.get<{
          sessionId: string;
          flow?: string;
          declaredIntent?: string;
          capturedContact?: { name?: string; email?: string };
          warehouses?: unknown[];
          flowState?: HollySessionFlowStateResponse;
        }>(`/hollyv4/session/${encodeURIComponent(threadId)}`, { baseURL: '/' });
        setPrefill({
          name: data.capturedContact?.name,
          email: data.capturedContact?.email,
          warehousesCount: Array.isArray(data.warehouses)
            ? data.warehouses.length
            : undefined,
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          transportQuote: extractTransportQuote(data.flowState),
        });
        setHollySession({
          flow: data.flow,
          declaredIntent: data.declaredIntent,
          flowState: data.flowState,
        });
        if (data.flow) {
          setJourney(mapFlowToJourney(data.flow));
        }
      } catch {
        // Hydration failure not fatal — register form just starts empty.
      }
      setFlowState('register');
    },
    [authHttp, journey]
  );

  // ── Submit: register, then handle Holly handoff client-side ─────────────────
  const handleRegister = useCallback(
    async (values: RegisterFormValues) => {
      setServerError('');

      try {
        const role = deriveRoleForBackend(journey);
        const { data } = await authHttp.post<RegisterApiResult>(
          REGISTRATION_URL,
          {
            // Fields the spec keeps (§5.1).
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            hasAgreedToTandC: values.hasAgreedToTandC,
            // Backend still requires these for FusionAuth group/role creation; pre-fill
            // sensible defaults derived from the journey until the org-modal spec lands.
            role,
            businessName: values.firstName + ' ' + values.lastName, // sole-trader stand-in
            // FusionAuth's E.164 validator rejects empty strings — send null so the
            // field is omitted entirely. The 4-field form doesn't capture phone.
            mobilePhoneNumber: null,
            baseUrl: window.location.origin,
            isSkippingRegistrationEmail: false,
          }
        );

        if (!data.registrationResult?.wasSuccessful) {
          setServerError(
            data.registrationResult?.errors?.[0] || 'Registration failed'
          );
          return;
        }

        // Mirror LegacyRegistration's HubSpot lead sync so accounts created
        // through the new /register flow still land in HubSpot. Prod-only and
        // fire-and-forget — a HubSpot failure must never block the user
        // reaching the success screen.
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        submitRegistrationToHubSpot(values, role).catch(() => {});

        // Holly 3.0 Reg spec §6 — frontend owns the post-registration destination
        // because it already fetched the Holly session for prefill. Falls back to
        // the journey hint when no session is available.
        //
        // Holly's register-link.ts (v3/v4 identical) constructs an explicit `?redirect=` URL
        // param (e.g. `/warehouse-shortlist?source=holly`) that carries the
        // `source=holly` signal the shortlist page uses to gate its
        // spinner+poll on the async backend match-build. Honour it when present
        // (safe-path-validated) so we don't drop that signal when the Holly
        // session lookup hasn't hydrated (no hollyState param in the URL).
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const safeRedirect = readSafeRedirectParam(searchParams);
        const dest =
          safeRedirect ??
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          computeRedirectTo(hollySession, journey, hollySessionId);
        setRedirectTo(dest);

        // Holly 3.0 Reg spec §7.4 — link the new FusionAuth user to the Holly
        // session and clear the 72h expiry. Fire-and-forget; failure must not
        // block the user reaching the success screen. Goes through the Koa
        // gateway, not the .NET backend.
        if (hollySessionId && data.registrationResult.user?.id) {
          authHttp
            .post(
              '/hollyv4/registration-complete',
              {
                threadId: hollySessionId,
                userId: data.registrationResult.user.id,
                email: values.email,
                role,
              },
              { baseURL: '/' }
            )
            .catch(() => {
              /* swallowed — gateway already soft-fails on Holly errors */
            });
        }

        if (data.auth?.accessToken && data.registrationResult.user) {
          setUser(data.registrationResult.user as never);
          // Don't auto-navigate yet — show the success screen briefly.
          logIn(
            data.registrationResult.user as never,
            data.auth as never,
            dest
          );
        }
        setFlowState('success');
      } catch (err) {
        const ax = err as {
          response?: { status?: number; data?: { errors?: string[] } };
        };
        if (ax?.response?.status === 423) {
          setServerError(
            'Your account is pending approval. We have emailed your admin.'
          );
        } else if (ax?.response?.status === 409) {
          setServerError(
            'An account with this email already exists. Try logging in.'
          );
        } else {
          setServerError(
            ax?.response?.data?.errors?.[0] || 'Registration failed'
          );
        }
      }
    },
    [authHttp, hollySessionId, journey, hollySession, logIn, setUser, searchParams]
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className='min-h-screen flex flex-col bg-gray-50'>
      <Header onLogin={() => navigate('/login')} />
      <main className='flex-1 flex items-start justify-center px-4 py-10'>
        {flowState === 'classifying' && <ClassifyingSpinner />}
        {(flowState === 'router' || flowState === 'holly-chat') && (
          <RouterScreen
            onPick={(j) => {
              setJourney(j);
              setFlowState('holly-chat');
            }}
          />
        )}
        {flowState === 'holly-chat' && (
          <AISidebar
            controlledOpen
            // §4.4 — "Something else" enters Holly in undeclared (full discovery) mode.
            declaredIntent={journey === 'else' ? undefined : journey}
            initialMessage={ROUTER_PROMPTS[journey]}
            onClose={() => setFlowState('router')}
            onReadyToRegister={handleReadyToRegister}
          />
        )}
        {flowState === 'welcome-back' && classification && (
          <WelcomeBackScreen
            onLogIn={() => navigate('/login')}
            onFresh={() => setFlowState('router')}
          />
        )}
        {flowState === 'register' && (
          <RegisterFormScreen
            journey={journey}
            prefill={prefill}
            onSubmit={handleRegister}
            onBack={() => setFlowState('router')}
            serverError={serverError}
          />
        )}
        {flowState === 'success' && (
          <SuccessScreen
            redirectTo={redirectTo}
            journey={journey}
            onGo={() => navigate(redirectTo)}
          />
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header (logo + Log-in link)
// ─────────────────────────────────────────────────────────────────────────────

function Header({ onLogin }: { onLogin: () => void }) {
  return (
    <header className='flex items-center justify-between px-6 lg:px-12 py-5 border-b border-gray-200 bg-white'>
      <img src={FLOX_LOGO_BLUE_SRC} alt='FLOX' className='h-7' />
      <div className='text-sm text-gray-600'>
        Already have an account?{' '}
        <button
          onClick={onLogin}
          className='text-blue-600 font-semibold hover:underline'
          type='button'
        >
          Log in
        </button>
      </div>
    </header>
  );
}

function ClassifyingSpinner() {
  return (
    <div className='flex flex-col items-center gap-3 mt-32'>
      <FloxSpinner />
      <div className='text-sm text-gray-500'>Just a moment…</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Router screen (§4)
// ─────────────────────────────────────────────────────────────────────────────

function RouterScreen({ onPick }: { onPick: (j: Journey) => void }) {
  const options: Array<{
    key: Journey;
    label: string;
    desc: string;
    Icon: typeof TruckIcon;
  }> = [
    {
      key: 'warehouse_search',
      label: 'Find warehousing',
      desc: 'Search storage, 3PL and fulfilment across the UK and EU',
      Icon: BuildingOfficeIcon,
    },
    {
      key: 'transport_booking',
      label: 'Book transport',
      desc: 'Get a quote for moving goods — pallets, parcels, full loads',
      Icon: TruckIcon,
    },
    {
      key: 'lsp_listing',
      label: 'List my services',
      desc: 'Offer your warehouse space or transport capacity to buyers',
      Icon: Squares2X2Icon,
    },
    {
      key: 'else',
      label: 'Something else',
      desc: 'Not sure yet — Holly will help you figure it out',
      Icon: MapIcon,
    },
  ];
  return (
    <div className='w-full max-w-3xl px-2'>
      <div className='inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5'>
        <SparklesIcon className='w-4 h-4' />
        Welcome to FLOX
      </div>
      <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-3'>
        What brings you to FLOX today?
      </h1>
      <p className='text-base text-gray-600 mb-8 max-w-xl'>
        Pick what you&apos;re here to do — Holly, our AI concierge, will take it
        from there.
      </p>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {options.map((opt) => (
          <button
            key={opt.key}
            type='button'
            onClick={() => onPick(opt.key)}
            className='group flex items-center gap-4 p-5 bg-white border border-gray-200 rounded-xl text-left hover:border-blue-500 hover:shadow-md hover:-translate-y-0.5 transition-all min-h-[104px]'
          >
            <div className='w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors'>
              <opt.Icon className='w-6 h-6' />
            </div>
            <div className='flex-1'>
              <div className='text-base font-semibold text-gray-900 mb-1'>
                {opt.label}
              </div>
              <div className='text-sm text-gray-500 leading-snug'>
                {opt.desc}
              </div>
            </div>
            <ArrowRightIcon className='w-5 h-5 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all' />
          </button>
        ))}
      </div>
      <div className='mt-6 text-center'>
        <Link
          to='/register-manually'
          className='text-sm font-medium text-blue-600 hover:text-blue-800 underline transition-colors'
        >
          I&apos;d like to register manually
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Welcome-back screen (§3.3)
// ─────────────────────────────────────────────────────────────────────────────

function WelcomeBackScreen({
  onLogIn,
  onFresh,
}: {
  onLogIn: () => void;
  onFresh: () => void;
}) {
  return (
    <div className='w-full max-w-xl px-2'>
      <div className='inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5'>
        <SparklesIcon className='w-4 h-4' />
        We recognise you
      </div>
      <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-3'>
        Welcome back.
      </h1>
      <p className='text-base text-gray-700 mb-8 leading-relaxed'>
        We have an account for you on this browser. Log in to pick up where
        you left off.
      </p>
      <div className='flex flex-col gap-3'>
        <Button
          intent='primary'
          size='lg'
          onClick={onLogIn}
          label='Log in'
          endIcon={<ArrowRightIcon className='w-5 h-5' />}
        />
        <Button
          intent='secondary'
          size='lg'
          onClick={onFresh}
          label='Start fresh'
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Register form screen (§5)
// ─────────────────────────────────────────────────────────────────────────────

function RegisterFormScreen({
  journey,
  prefill,
  onSubmit,
  onBack,
  serverError,
}: {
  journey: Journey;
  prefill: HollySessionPrefill;
  onSubmit: (values: RegisterFormValues) => Promise<void>;
  onBack: () => void;
  serverError: string;
}) {
  const copy = JOURNEY_COPY[journey];

  const { firstName, lastName } = useMemo(() => {
    if (!prefill.name) return { firstName: '', lastName: '' };
    const parts = prefill.name.trim().split(/\s+/);
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
  }, [prefill.name]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      firstName,
      lastName,
      email: prefill.email || '',
      password: '',
      hasAgreedToTandC: false,
    },
  });

  return (
    <div className='w-full max-w-md px-2'>
      <button
        type='button'
        onClick={onBack}
        className='flex items-center gap-1 text-sm text-gray-500 font-semibold mb-5 hover:text-gray-700'
      >
        <ArrowLeftIcon className='w-4 h-4' />
        Back
      </button>

      <div className='inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4'>
        <span className='w-1.5 h-1.5 rounded-full bg-blue-600' />
        {copy.badge}
      </div>

      <h1 className='text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-3'>
        {copy.headline}
      </h1>
      <p className='text-sm text-gray-600 mb-7 leading-relaxed'>
        {copy.subcopy(prefill.warehousesCount ?? 12)}
      </p>

      {journey === 'transport_booking' && prefill.transportQuote && (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <TransportQuoteBanner quote={prefill.transportQuote} />
      )}

      <form
        onSubmit={handleSubmit((vals) => onSubmit(vals))}
        noValidate
      >
        <div className='grid grid-cols-2 gap-3'>
          <Controller
            name='firstName'
            control={control}
            render={({ field }) => (
              <Field
                label='First name'
                Icon={UserIcon}
                error={errors.firstName?.message}
                prefilled={!!prefill.name}
                {...field}
              />
            )}
          />
          <Controller
            name='lastName'
            control={control}
            render={({ field }) => (
              <Field
                label='Last name'
                Icon={UserIcon}
                error={errors.lastName?.message}
                prefilled={!!prefill.name}
                {...field}
              />
            )}
          />
        </div>
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <Field
              type='email'
              label='Email'
              Icon={EnvelopeIcon}
              error={errors.email?.message}
              prefilled={!!prefill.email}
              {...field}
            />
          )}
        />
        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <Field
              type='password'
              label='Create password'
              placeholder='At least 10 characters'
              Icon={LockClosedIcon}
              error={errors.password?.message}
              {...field}
            />
          )}
        />

        <Controller
          name='hasAgreedToTandC'
          control={control}
          render={({ field }) => (
            <label className='flex items-start gap-3 text-sm text-gray-600 leading-relaxed mb-5'>
              <input
                type='checkbox'
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className='mt-1'
              />
              <span>
                By creating an account, you agree to our{' '}
                <a className='text-blue-600 font-semibold' href='/terms'>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a className='text-blue-600 font-semibold' href='/privacy'>
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          )}
        />
        {errors.hasAgreedToTandC && (
          <p className='text-sm text-red-600 mb-3 -mt-3'>
            {errors.hasAgreedToTandC.message}
          </p>
        )}

        {serverError && (
          <div className='flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 mb-4'>
            <ExclamationTriangleIcon className='w-5 h-5 shrink-0 mt-0.5' />
            <div>{serverError}</div>
          </div>
        )}

        <Button
          intent='primary'
          size='lg'
          type='submit'
          disabled={isSubmitting}
          className='w-full'
          label={isSubmitting ? 'Creating your account…' : copy.cta}
          endIcon={<ArrowRightIcon className='w-5 h-5' />}
        />
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  Icon: typeof UserIcon;
  type?: string;
  placeholder?: string;
  prefilled?: boolean;
  error?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  name?: string;
}

// Spec §6.2 — quote summary banner shown above the transport_booking register
// form. Hydrated from Holly's flowState.quoteParameters + estimateRange. Each
// part renders only when its data is present, so partial captures still look ok.
function TransportQuoteBanner({ quote }: { quote: TransportQuotePrefill }) {
  const route =
    quote.origin && quote.destination
      ? `${quote.origin} → ${quote.destination}`
      : quote.origin || quote.destination || null;
  const palletsLabel =
    typeof quote.pallets === 'number' ? `${quote.pallets} pallets` : null;
  const subParts = [route, palletsLabel, quote.date].filter((x): x is string =>
    Boolean(x)
  );
  const priceRange =
    typeof quote.estimateLow === 'number' &&
    typeof quote.estimateHigh === 'number'
      ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
        formatPriceRange(quote.estimateLow, quote.estimateHigh, quote.currency)
      : null;

  return (
    <div className='bg-blue-50 rounded-lg p-4 mb-6'>
      <div className='text-xs font-bold text-primary uppercase tracking-wider mb-1.5'>
        Your quote from Holly
      </div>
      {subParts.length > 0 && (
        <div className='text-sm font-semibold text-gray-900'>
          {subParts.map((part, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i}>
              {i > 0 && <span className='text-gray-400'> · </span>}
              {part}
            </span>
          ))}
        </div>
      )}
      {priceRange && (
        <div className='mt-1 text-xl font-bold text-flox-blue'>
          {priceRange}
        </div>
      )}
    </div>
  );
}

function formatPriceRange(
  low: number,
  high: number,
  currency?: string
): string {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const symbol = currencySymbol(currency);
  const fmt = (n: number) => Math.round(n).toLocaleString('en-GB');
  return `${symbol}${fmt(low)} – ${symbol}${fmt(high)}`;
}

function currencySymbol(currency?: string): string {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
    case undefined:
    case '':
      return '£';
    default:
      return `${currency} `;
  }
}

function Field({
  label,
  Icon,
  type = 'text',
  placeholder,
  prefilled,
  error,
  ...field
}: FieldProps) {
  return (
    <div className='mb-4'>
      <div className='flex items-center justify-between mb-1.5'>
        <label className='text-sm font-semibold text-gray-700'>{label}</label>
        {prefilled && (
          <span className='inline-flex items-center gap-1 text-xs text-emerald-600 font-medium'>
            <CheckIcon className='w-3 h-3' /> From Holly
          </span>
        )}
      </div>
      <div className='relative'>
        <Icon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none' />
        <input
          type={type}
          placeholder={placeholder}
          aria-label={label}
          className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${
            error
              ? 'border-red-300 bg-red-50'
              : prefilled
              ? 'border-blue-200 bg-blue-50/40'
              : 'border-gray-200 bg-white'
          }`}
          {...field}
        />
      </div>
      {error && <p className='text-xs text-red-600 mt-1'>{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success screen
// ─────────────────────────────────────────────────────────────────────────────

function SuccessScreen({
  redirectTo,
  journey,
  onGo,
}: {
  redirectTo: string;
  journey: Journey;
  onGo: () => void;
}) {
  // Auto-navigate after a brief moment so the success state is acknowledged
  // but the user isn't stuck.
  useEffect(() => {
    const t = setTimeout(onGo, 1800);
    return () => clearTimeout(t);
  }, [onGo]);
  const target =
    journey === 'warehouse_search'
      ? 'your shortlist'
      : journey === 'transport_booking'
      ? 'your booking'
      : journey === 'lsp_listing'
      ? 'listing setup'
      : 'the right place';
  return (
    <div className='w-full max-w-md text-center pt-12'>
      <div className='w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6'>
        <CheckIcon className='w-8 h-8 text-emerald-600' />
      </div>
      <h1 className='text-2xl lg:text-3xl font-bold text-gray-900 mb-3'>
        Account created.
      </h1>
      <p className='text-sm text-gray-600 mb-6 leading-relaxed'>
        We&apos;ve sent a verification email. You can verify later — taking you
        to {target} now.
      </p>
      <Button
        intent='primary'
        size='lg'
        onClick={onGo}
        label='Take me there'
        endIcon={<ArrowRightIcon className='w-5 h-5' />}
      />
      <p className='text-xs text-gray-400 mt-3'>
        Redirecting to <code>{redirectTo}</code>…
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function mapFlowToJourney(flow: string): Journey {
  switch (flow) {
    case 'warehouse_search':
      return 'warehouse_search';
    case 'transport_booking':
      return 'transport_booking';
    case 'lsp_listing':
      return 'lsp_listing';
    default:
      return 'else';
  }
}

/**
 * Pull the transport quote summary out of Holly's session flowState for the
 * "Your quote from Holly" banner. Holly (v3/v4 identical) emits a flat TransportBookingFlowState
 * (origin/destination/loadSize/timing/estimateRange), so we delegate to the shared
 * mapper that the create-transport page also uses — keeping banner and form prefill
 * reading from one source of truth.
 */
function extractTransportQuote(
  flowState: RawHollyTransportFlowState | undefined
): TransportQuotePrefill | undefined {
  const summary = toTransportQuoteSummary(flowState);
  if (!summary) return undefined;
  return {
    origin: summary.origin,
    destination: summary.destination,
    pallets: typeof summary.pallets === 'number' ? summary.pallets : undefined,
    date: summary.date,
    estimateLow: summary.estimateLow,
    estimateHigh: summary.estimateHigh,
    currency: summary.currency,
  };
}

/**
 * Holly chat → register handoff via legacy URL params. When Holly emits a
 * register CTA in chat (HTML <a href> link), the URL carries email, name, and
 * role on the query string. We treat any of these as a handoff signal, hydrate
 * prefill from the URL, and skip the classifier so the user lands on the
 * journey-themed register form.
 *
 * Journey resolution order:
 *  1. Explicit `?journey=` (canonical, set by Holly once spec §B ships)
 *  2. Inferred from `role`: logistics-provider → lsp_listing,
 *     logistics-client → warehouse_search (default for shippers — transport
 *     bookings need an explicit `?journey=transport_booking` because the
 *     existing role+redirect URL params can't differentiate them).
 */
function parseUrlHandoff(
  searchParams: URLSearchParams
): { prefill: HollySessionPrefill; journey: Journey } | null {
  const email = searchParams.get('email')?.trim() || undefined;
  const name = searchParams.get('name')?.trim() || undefined;
  const role = searchParams.get('role')?.trim() || undefined;
  const journeyParam = searchParams.get('journey')?.trim() || undefined;

  if (!email && !name && !role && !journeyParam) return null;

  const journey: Journey =
    parseJourneyParam(journeyParam) ?? inferJourneyFromRole(role) ?? 'else';

  return {
    prefill: { name, email },
    journey,
  };
}

function parseJourneyParam(value: string | undefined): Journey | null {
  switch (value) {
    case 'warehouse_search':
    case 'transport_booking':
    case 'lsp_listing':
    case 'else':
      return value;
    default:
      return null;
  }
}

function inferJourneyFromRole(role: string | undefined): Journey | null {
  if (role === 'logistics-provider' || role === 'lsp') return 'lsp_listing';
  if (role === 'logistics-client' || role === 'shipper')
    return 'warehouse_search';
  return null;
}

/**
 * Until the §6.4–§6.5 organisation modal lands, the backend still requires a
 * role to create the FusionAuth group. Map journey → role:
 *  - lsp_listing → WarehouseManager (provider side)
 *  - everything else → WarehouseClient (shipper side).
 *
 * The user can refine this from settings post-registration; this is a placeholder
 * decision until the Org modal becomes the canonical source of "what kind of
 * account am I?".
 */
function deriveRoleForBackend(journey: Journey): string {
  return journey === 'lsp_listing' ? 'WarehouseManager' : 'WarehouseClient';
}

// Same HubSpot Forms API endpoint (portal 3810753) used by LegacyRegistration.
const HUBSPOT_REGISTRATION_FORM_URL =
  'https://api.hsforms.com/submissions/v3/integration/submit/3810753/fd613e6a-cac7-450e-941e-2c05c8c69bc0';

const PRODUCTION_HOST = 'app.flox.is';

function isProductionEnvironment(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.location.hostname === PRODUCTION_HOST
  );
}

/**
 * Push a newly-registered account to HubSpot as an "Account created" lead.
 * Never throws — callers fire-and-forget. Gated to the production host on
 * purpose: stage and prod run the SAME built bundle, so a build-time flag
 * (NODE_ENV / import.meta.env) reads "production" on stage too and would leak
 * stage sign-ups to HubSpot. `company` falls back to the sole-trader stand-in.
 */
async function submitRegistrationToHubSpot(
  values: RegisterFormValues,
  role: string
): Promise<void> {
  if (!isProductionEnvironment()) return;

  try {
    await fetch(HUBSPOT_REGISTRATION_FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        submittedAt: Date.now(),
        fields: [
          { objectTypeId: '0-1', name: 'email', value: values.email },
          { objectTypeId: '0-1', name: 'firstname', value: values.firstName },
          { objectTypeId: '0-1', name: 'lastname', value: values.lastName },
          {
            objectTypeId: '0-1',
            name: 'company',
            value: `${values.firstName} ${values.lastName}`,
          },
          {
            objectTypeId: '0-1',
            name: 'client_type',
            value:
              role === 'WarehouseManager'
                ? 'Seller (LSP)'
                : 'Buyer (Shipper)',
          },
          {
            objectTypeId: '0-1',
            name: 'funnel_stage',
            value: 'Account created',
          },
          {
            objectTypeId: '0-1',
            name: 'lead_source',
            value: 'FLOX Registration',
          },
        ],
      }),
    });
  } catch (error) {
    console.error('HubSpot registration sync failed', error);
  }
}

interface ActiveHollySession {
  sessionId: string;
  lastActiveAt?: string | null;
}

interface HollySessionResponse {
  sessionId?: string;
  status?: string | null;
  expiresAt?: string | null;
  lastActiveAt?: string | null;
}

type AuthHttp = ReturnType<typeof useAuthHttp>;

/**
 * Frontend equivalent of the prior `ClassifyVisitor` Mechanism A — iterate the
 * sessionIds the browser has accumulated in localStorage, ask the gateway for
 * each via `GET /hollyv4/session/:id`, and return the first one that's still
 * `active` and unexpired so we can resume the user's Holly conversation.
 *
 * Order is preserved (most-recent first, by the session-store writer's
 * convention), and any per-session error is swallowed — we just move on to
 * the next id rather than failing the whole bootstrap.
 */
async function findActiveHollySession(
  authHttp: AuthHttp,
  sessionIds: string[]
): Promise<ActiveHollySession | null> {
  for (const id of sessionIds) {
    try {
      const { data } = await authHttp.get<HollySessionResponse>(
        `/hollyv4/session/${encodeURIComponent(id)}`,
        { baseURL: '/' }
      );
      const notExpired =
        !data.expiresAt || new Date(data.expiresAt).getTime() > Date.now();
      if (data.status === 'active' && notExpired) {
        return {
          sessionId: data.sessionId ?? id,
          lastActiveAt: data.lastActiveAt ?? null,
        };
      }
    } catch {
      // Continue scanning the rest — Holly might just have GC'd this one.
    }
  }
  return null;
}

/**
 * Read the `?redirect=` URL param and return it ONLY when it's a same-origin
 * relative path. Guards against:
 *  - absolute URLs (`https://evil.com/...`) and protocol-relative (`//evil.com`)
 *  - scheme-only (`javascript:`, `data:`)
 *  - missing param
 *
 * The param is intentionally URL-decoded by `URLSearchParams.get` already.
 * We require it to start with `/` and not look like `//` (protocol-relative).
 */
function readSafeRedirectParam(
  searchParams: URLSearchParams
): string | null {
  const raw = searchParams.get('redirect');
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null; // e.g. /javascript:foo
  return trimmed;
}

/**
 * Holly 3.0 Reg spec §6 — destination URL the user lands on after a successful
 * /Auth/Register. Mirrors the cascade previously encoded server-side:
 *  1. The Holly session's `flow` + `flowState` (deep-link with shortlistId /
 *     quoteId / declaredTrack when Holly captured them).
 *  2. The session's `declaredIntent` from the router (if Holly hasn't yet
 *     classified a task).
 *  3. The frontend's journey hint (when no session was hydrated, e.g. mock
 *     ids, lookup failures, or direct registrations).
 *  4. `/noticeboard` as a final safe fallback.
 */
function computeRedirectTo(
  session: HollySessionForRedirect | null,
  journey: Journey,
  hollySessionId?: string | null
): string {
  // Explicit session id lets the create-transport page hydrate prefill without
  // relying on the localStorage fallback in useHollySession.
  const sessionParam = hollySessionId
    ? `&hollySessionId=${encodeURIComponent(hollySessionId)}`
    : '';
  if (session?.flow) {
    const fs = session.flowState;
    switch (session.flow) {
      case 'warehouse_search':
        // `source=holly` is the signal the shortlist page uses to show a
        // spinner + poll Matches/List while the backend finishes building
        // the shortlist post-registration (handler is async).
        return fs?.shortlistId
          ? `/warehouse-shortlist?shortlistId=${fs.shortlistId}&source=holly`
          : '/warehouse-shortlist?source=holly';
      case 'transport_booking':
        return fs?.quoteId
          ? `/transport-order/create?quoteId=${fs.quoteId}&source=holly${sessionParam}`
          : `/transport-order/create?source=holly${sessionParam}`;
      case 'lsp_listing':
        return fs?.declaredTrack
          ? `/app/listings/add?track=${fs.declaredTrack}`
          : '/app/listings/add';
      default:
        break;
    }
  }
  const intent = (session?.declaredIntent as Journey | undefined) ?? journey;
  // Tack on source=holly only when a Holly session is the source of the hint
  // — pure journey-hint redirects (no session) should not activate the
  // shortlist polling because there's no Holly conversation to wait on.
  const hasSession = session != null;
  switch (intent) {
    case 'warehouse_search':
      return hasSession ? '/warehouse-shortlist?source=holly' : '/warehouse-shortlist';
    case 'transport_booking':
      return hasSession
        ? `/transport-order/create?source=holly${sessionParam}`
        : '/transport-order/create';
    case 'lsp_listing':
      return '/app/listings/add';
    default:
      return '/noticeboard';
  }
}
